import type { TransformSpec } from './transform.js';

/**
 * Photosynthesis as a bookkeeping claim: 6 CO₂ + 6 H₂O + light → C₆H₁₂O₆ + 6 O₂.
 * The ledger counts atoms on both sides — carbon 6/6, hydrogen 12/12, oxygen
 * 18/18 — and the compiler refuses the figure if they ever disagree. Light is
 * marked a driver: it is consumed, and it is not one of the conserved things.
 */
export function transformFixture(width = 640): TransformSpec {
  return {
    id: 'photosynthesis-mechanism',
    family: 'transform',
    view: 'schematic',
    viewport: { width, height: 500 },
    subject: 'Photosynthesis',
    inputs: [
      { id: 'carbon-dioxide', label: 'Carbon dioxide', amount: '6 CO₂', carries: { C: 6, O: 12 } },
      { id: 'water', label: 'Water', amount: '6 H₂O', carries: { H: 12, O: 6 } },
      { id: 'light', label: 'Light energy', amount: 'photons', driver: true },
    ],
    process: { label: 'Chloroplast', detail: 'chlorophyll captures light and rebuilds the bonds' },
    outputs: [
      { id: 'glucose', label: 'Glucose', amount: 'C₆H₁₂O₆', carries: { C: 6, H: 12, O: 6 } },
      { id: 'oxygen', label: 'Oxygen', amount: '6 O₂', carries: { O: 12 } },
    ],
    conserved: ['C', 'H', 'O'],
    law: 'Atoms are rearranged, never created · energy drives the change',
    title: 'The atoms that leave are the atoms that arrived',
    subtitle: 'A mechanism rearranges bonds; the ledger proves nothing was created or destroyed on the way through.',
    provenance: 'Net photosynthesis equation 6 CO₂ + 6 H₂O → C₆H₁₂O₆ + 6 O₂; atom counts follow from the formulae',
  };
}
